import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, ShoppingCart, Trash2, ExternalLink, RefreshCw, PackageCheck, X, Plus, Search, MapPin, Navigation, Loader, Camera } from "lucide-react";
import { formatErr, fmtDate } from "@/lib/api";
import { ColonyBadge } from "@/lib/colonyColors";
import { ShoppingAiButton } from "@/components/ShoppingAiButton";
import { CalendarYearSelect } from "@/components/CalendarYearSelect";
import { useCalendarPage } from "@/lib/useCalendarPage";
import PhotoAddButtons, { pasteToFiles } from "@/components/PhotoAddButtons";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { processMediaFiles } from "@/lib/livePhotoMedia";
import { readTakenAt } from "@/lib/photoTimes";
import { ProductTimeline } from "@/components/ProductTimeline";

// Small "view drop-off photo" chip (used on ticket strips). Opens a dialog
// showing the photo(s) plus WHO dropped it off and WHEN. Tap a photo to zoom.
function DropoffPhotoButton({ client, basePath, itemId, count = 0, T = (x) => x }) {
  const opener = useRef(null);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  if (!count || !client) return null;
  const view = async () => {
    if (data) { setOpen(true); return; }
    setBusy(true);
    try {
      const { data: d } = await client.get(`${basePath}/shopping-list/${itemId}/dropoff-photos`);
      setData(d);
      setOpen(true);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        type="button"
        ref={opener}
        onClick={view}
        disabled={busy}
        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
        title={T("View drop-off photo")}
        data-testid={`dropoff-photo-btn-${itemId}`}
      >
        <Camera className="w-3 h-3" /> {count}
      </button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent data-testid={`dropoff-view-${itemId}`} onCloseAutoFocus={event => {
          if (opener.current?.isConnected) { event.preventDefault(); opener.current.focus({ preventScroll: true }); }
        }}>
          <DialogHeader>
            <DialogTitle>{T("Drop-off photo")}</DialogTitle>
            {data?.by && (
              <DialogDescription data-testid={`dropoff-whowhen-${itemId}`}>
                {T("Dropped off by")} {data.by}{data.updated_at ? ` · ${fmtDate(data.updated_at)}` : ""}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {(data?.photos || []).map((src, i) => (
              <LivePhotoSurface
                key={i}
                value={src}
                alt=""
                onActivate={() => setZoom(i)}
                className="w-24 h-24 rounded-sm border border-border cursor-zoom-in"
                testId={`dropoff-view-photo-${itemId}-${i}`}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {zoom != null && data?.photos?.length > 0 && (
        <PhotoLightbox photos={data.photos} start={zoom} alt="Drop-off photo" onClose={() => setZoom(null)} />
      )}
    </>
  );
}

// Drop-off photo control shown on a PURCHASED item (separate from the
// purchase checkbox). The buyer taps this camera LATER, when they actually
// drop the item off, to attach/view the proof photo(s). Up to 3.
function DropoffManager({ client, basePath, item, required, onUpdated, T = (x) => x }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [takenAt, setTakenAt] = useState(null);
  useEffect(() => { if (!photos.length) setTakenAt(null); }, [photos.length]);
  const count = item.dropoff_photo_count || 0;
  const hasPhotos = count > 0;
  const needs = required && !hasPhotos;

  const openDialog = async () => {
    setOpen(true);
    setPhotos([]);
    setTakenAt(null);
    if (hasPhotos && client) {
      setLoading(true);
      try {
        const { data } = await client.get(`${basePath}/shopping-list/${item.id}/dropoff-photos`);
        setPhotos(data.photos || []);
      } catch (e) {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const addFiles = async (fileList) => {
    if (!fileList?.length) return;
    // Copy the files BEFORE awaiting: the input clears its live FileList right after onFiles.
    const files = Array.from(fileList);
    setBusy(true);
    try {
      const snappedTimes = (await Promise.all(files.map(readTakenAt))).filter(Boolean).sort();
      const result = await processMediaFiles(files, { max: 3, existingCount: photos.length, client, scope: basePath.includes("colony") || window.location.pathname.includes("managementusa") ? "mgmt_usa_nj" : "pool" });
      setPhotos((prev) => [...prev, ...result.items].slice(0, 3));
      if (snappedTimes.length) setTakenAt((prev) => prev || snappedTimes[0]);
      if (result.ignoredVideos) toast.message("Select the MOV together with its matching iPhone photo.");
    } catch (error) {
      toast.error(error.message || "Could not add that photo.");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`${basePath}/shopping-list/${item.id}`, { dropoff_photos: photos, ...(takenAt ? { dropoff_photo_taken_at: takenAt } : {}) });
      onUpdated?.(data);
      toast.success(photos.length ? T("Drop-off photo saved") : T("Drop-off photos removed"));
      setOpen(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const btnClass = hasPhotos
    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
    : needs
      ? "border-amber-400 bg-amber-50 text-amber-700 animate-pulse"
      : "border-border text-muted-foreground";

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className={`w-8 h-8 rounded-sm border flex items-center justify-center relative shrink-0 transition hover:brightness-95 ${btnClass}`}
        title={hasPhotos ? T("View / edit drop-off photo") : (required ? T("Drop-off photo needed") : T("Add drop-off photo"))}
        data-testid={`dropoff-camera-${item.id}`}
      >
        <Camera className="w-4 h-4" />
        {hasPhotos && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold flex items-center justify-center">
            {count}
          </span>
        )}
        {needs && (
          <span className="absolute -top-1.5 -right-1.5 w-[15px] h-[15px] rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">!</span>
        )}
      </button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent onPaste={pasteToFiles(addFiles)} data-testid={`dropoff-dialog-${item.id}`}>
          <DialogHeader>
            <DialogTitle>{T("Drop-off photo")}</DialogTitle>
            <DialogDescription>
              {T("Add a photo of where you dropped it off.")}{item?.product ? ` (${item.product})` : ""}
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-6 flex justify-center"><Loader className="w-5 h-5 animate-spin text-primary" /></div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {photos.map((src, i) => (
                <div key={i} className="relative">
                  <LivePhotoSurface value={src} alt="" className="w-16 h-16 rounded-sm border border-border" testId={`dropoff-preview-${item.id}-${i}`} />
                  <button
                    type="button"
                    onClick={() => setPhotos((p) => p.filter((_, x) => x !== i))}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-foreground text-background flex items-center justify-center"
                    title={T("Remove")}
                    data-testid={`dropoff-remove-${item.id}-${i}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {photos.length < 3 && <PhotoAddButtons onFiles={addFiles} testidPrefix={`dropoff-${item.id}`} />}
            </div>
          )}
          {hasPhotos && item.dropoff_by && (
            <p className="text-xs text-muted-foreground" data-testid={`dropoff-whowhen-mgr-${item.id}`}>
              {T("Dropped off by")} {item.dropoff_by}{item.dropoff_at ? ` · ${fmtDate(item.dropoff_at)}` : ""}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} data-testid={`dropoff-cancel-${item.id}`}>
              {T("Cancel")}
            </Button>
            <Button className="rounded-sm" disabled={busy || saving || loading} onClick={save} data-testid={`dropoff-save-${item.id}`}>
              {saving ? T("Saving…") : T("Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ROLE_LABEL = { colony: "Colony contact", worker: "Worker", office: "Office" };

// Generic (no-GPS-needed) product search links — always available per item.
const STORE_SEARCH = {
  "Home Depot": (q) => `https://www.homedepot.com/s/${encodeURIComponent(q)}`,
  "Ace Hardware": (q) => `https://www.acehardware.com/search?query=${encodeURIComponent(q)}`,
};

function StoreSearchLinks({ product, itemId, T = (x) => x }) {
  if (!product) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
      {Object.entries(STORE_SEARCH).map(([store, urlFn]) => (
        <a
          key={store}
          href={urlFn(product)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground hover:text-primary hover:border-primary/50 transition"
          data-testid={`shopping-item-${itemId}-search-${store.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <Search className="w-3 h-3" /> {T("Search on")} {store}
        </a>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// "Find nearby stores" — uses browser GPS to look up the closest Home
// Depot / Ace Hardware via the backend (free OSM Nominatim lookup), with
// a "Get directions" link that opens Google Maps.
// -----------------------------------------------------------------------
function NearbyStoresPanel({ client, basePath, T = (x) => x }) {
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState(null);
  const [open, setOpen] = useState(false);

  const findStores = () => {
    if (!navigator.geolocation) {
      toast.error(T("Your browser doesn't support location lookup."));
      return;
    }
    setLoading(true);
    setOpen(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await client.post(`${basePath}/shopping-list/nearby-stores`, {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
          });
          setStores(Array.isArray(data) ? data : []);
        } catch (e) {
          toast.error(formatErr(e.response?.data?.detail) || e.message);
          setOpen(false);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setLoading(false);
        setOpen(false);
        toast.error(T("Couldn't get your location — check your browser's location permission."));
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  return (
    <div>
      <Button
        variant="outline"
        size="sm"
        className="rounded-sm"
        onClick={findStores}
        disabled={loading}
        data-testid="shopping-find-nearby-stores"
      >
        {loading ? <Loader className="w-3.5 h-3.5 mr-2 animate-spin" /> : <MapPin className="w-3.5 h-3.5 mr-2" />}
        {loading ? T("Locating…") : T("Find nearby stores")}
      </Button>
      {open && !loading && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="nearby-stores-panel">
          {(stores || []).length === 0 ? (
            <Card className="p-3 text-sm text-muted-foreground border-dashed" data-testid="nearby-stores-empty">
              {T("No Home Depot or Ace Hardware found within about 30 miles.")}
            </Card>
          ) : (
            stores.map((s) => (
              <Card key={s.brand} className="p-3 flex items-start gap-2" data-testid={`nearby-store-${s.brand.toLowerCase().replace(/\s+/g, "-")}`}>
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{s.brand}</div>
                  <div className="text-xs text-muted-foreground">{s.address || T("Address unavailable")}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-medium">{s.distance_mi} {T("mi away")}</span>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      data-testid={`nearby-store-directions-${s.brand.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Navigation className="w-3 h-3" /> {T("Directions")}
                    </a>
                  </div>
                </div>
              </Card>
            ))
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-muted-foreground hover:text-foreground"
            title={T("Close")}
            data-testid="nearby-stores-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Add-to-shopping-list dialog. Portal-agnostic: pass the axios `client`
// and the exact POST `path` for the current portal.
//   admin  → client=api,       path=`/mgmt/colony-tickets/${tid}/shopping`
//   worker → client=api,       path=`/worker/tickets/${tid}/shopping`
//   colony → client=colonyApi, path=`/colony/tickets/${tid}/shopping`
// -----------------------------------------------------------------------
export function AddToShoppingDialog({ open, onOpenChange, client, path, ticketTitle = "", onAdded, T = (x) => x }) {
  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setProduct(""); setQuantity(""); setNote(""); }
  }, [open]);

  const submit = async () => {
    const p = product.trim();
    if (!p) { toast.error(T("Enter which product is needed.")); return; }
    setSaving(true);
    try {
      const { data } = await client.post(path, { product: p, quantity: quantity.trim(), note: note.trim() });
      toast.success(T("Added to shopping list."));
      onAdded && onAdded(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-shopping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" /> {T("Add to shopping list")}
          </DialogTitle>
          <DialogDescription>
            {ticketTitle
              ? `${T("Product needed for")}: ${ticketTitle}`
              : T("Add a product this ticket needs.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{T("Product")} *</Label>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={T("e.g. Chlorine tablets 3\"")}
              className="h-9 rounded-sm mt-1"
              data-testid="add-shopping-product"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">{T("Quantity")} ({T("optional")})</Label>
            <Input
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={T("e.g. 2 buckets")}
              className="h-9 rounded-sm mt-1"
              data-testid="add-shopping-quantity"
            />
          </div>
          <div>
            <Label className="text-xs">{T("Note")} ({T("optional")})</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={T("Any detail — brand, size, where to buy…")}
              className="rounded-sm mt-1 min-h-[70px]"
              data-testid="add-shopping-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="add-shopping-cancel">
            {T("Cancel")}
          </Button>
          <Button className="rounded-sm" onClick={submit} disabled={saving} data-testid="add-shopping-save">
            <ShoppingCart className="w-4 h-4 mr-2" /> {saving ? T("Adding…") : T("Add product")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
// Add an item straight from the Shopping List page itself — no ticket
// involved. Colony is optional (leave blank for a general item).
//   admin  → client=api, path="/mgmt/shopping-list"
//   worker → client=api, path="/worker/shopping-list"
// -----------------------------------------------------------------------
export function AddStandaloneShoppingDialog({ open, onOpenChange, client, path, colonies = [], onAdded, T = (x) => x }) {
  const [colonyId, setColonyId] = useState("none");
  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setColonyId("none"); setProduct(""); setQuantity(""); setNote(""); }
  }, [open]);

  const submit = async () => {
    const p = product.trim();
    if (!p) { toast.error(T("Enter which product is needed.")); return; }
    setSaving(true);
    try {
      const { data } = await client.post(path, {
        product: p,
        quantity: quantity.trim(),
        note: note.trim(),
        colony_id: colonyId === "none" ? null : colonyId,
      });
      toast.success(T("Added to shopping list."));
      onAdded && onAdded(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-standalone-shopping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" /> {T("Add item")}
          </DialogTitle>
          <DialogDescription>
            {T("Add a product straight to the shopping list — no ticket needed.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{T("Colony")} ({T("optional")})</Label>
            <Select value={colonyId} onValueChange={setColonyId}>
              <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="add-standalone-shopping-colony">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{T("No colony")}</SelectItem>
                {colonies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{T("Product")} *</Label>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={T("e.g. Chlorine tablets 3\"")}
              className="h-9 rounded-sm mt-1"
              data-testid="add-standalone-shopping-product"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">{T("Quantity")} ({T("optional")})</Label>
            <Input
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={T("e.g. 2 buckets")}
              className="h-9 rounded-sm mt-1"
              data-testid="add-standalone-shopping-quantity"
            />
          </div>
          <div>
            <Label className="text-xs">{T("Note")} ({T("optional")})</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={T("Any detail — brand, size, where to buy…")}
              className="rounded-sm mt-1 min-h-[70px]"
              data-testid="add-standalone-shopping-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="add-standalone-shopping-cancel">
            {T("Cancel")}
          </Button>
          <Button className="rounded-sm" onClick={submit} disabled={saving} data-testid="add-standalone-shopping-save">
            <ShoppingCart className="w-4 h-4 mr-2" /> {saving ? T("Adding…") : T("Add item")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -----------------------------------------------------------------------
// Shopping list view (admins + worker managers only).
//   admin  → client=api, basePath="/mgmt"
//   worker → client=api, basePath="/worker"
// -----------------------------------------------------------------------
export function ShoppingListView({ client, basePath, onOpenTicket, allColonies = [], T = (x) => x }) {
  const yearEnabled = basePath === "/worker";
  const shoppingPage = useCalendarPage({ client, path: `${basePath}/shopping-list`, yearEnabled });
  const { items, setItems, loading, reload: load } = shoppingPage;
  const [busy, setBusy] = useState(null);
  const [colonyFilter, setColonyFilter] = useState("all");
  const [sortByColony, setSortByColony] = useState(false);
  const [hidePurchased, setHidePurchased] = useState(true);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [dropoffRequired, setDropoffRequired] = useState(false);

  useEffect(() => {
    client.get(`${basePath}/shopping-dropoff-policy`)
      .then((r) => setDropoffRequired(!!r.data?.required))
      .catch(() => {});
  }, [client, basePath]);

  useEffect(() => {
    const updateTrackedItem = (event) => {
      const updated = event.detail;
      if (!updated?.id) return;
      setItems((previous) => previous.map((item) => item.id === updated.id ? updated : item));
    };
    window.addEventListener("shopping-product-tracked", updateTrackedItem);
    return () => window.removeEventListener("shopping-product-tracked", updateTrackedItem);
  }, [setItems]);

  const colonies = useMemo(() => {
    const map = new Map();
    items.forEach((it) => { if (it.colony_id) map.set(it.colony_id, it.colony_name || "—"); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [items]);

  const visible = useMemo(() => {
    let out = items.slice();
    if (colonyFilter !== "all") out = out.filter((it) => it.colony_id === colonyFilter);
    // A purchased item only hides once its drop-off is "settled" — i.e. a
    // photo was added, or (when optional) the buyer chose to skip the photo.
    if (hidePurchased) {
      out = out.filter(
        (it) => it.status !== "purchased" || !(((it.dropoff_photo_count || 0) > 0) || it.dropoff_skipped),
      );
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((it) =>
        (it.product || "").toLowerCase().includes(q) ||
        (it.note || "").toLowerCase().includes(q) ||
        (it.colony_name || "").toLowerCase().includes(q)
      );
    }
    out.sort((a, b) => {
      if (sortByColony) {
        const c = (a.colony_name || "").localeCompare(b.colony_name || "");
        if (c !== 0) return c;
      }
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return out;
  }, [items, colonyFilter, hidePurchased, sortByColony, search]);

  const neededCount = items.filter((it) => it.status !== "purchased").length;

  const skipDropoff = async (it) => {
    setBusy(it.id);
    try {
      const { data } = await client.patch(`${basePath}/shopping-list/${it.id}`, { dropoff_skipped: true });
      setItems((prev) => prev.map((x) => (x.id === it.id ? data : x)));
      toast.success(T("Skipped — you can still add a photo later from the camera"));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const togglePurchased = async (it) => {
    setBusy(it.id);
    const next = it.status === "purchased" ? "needed" : "purchased";
    try {
      const { data } = await client.patch(`${basePath}/shopping-list/${it.id}`, { status: next });
      setItems((prev) => prev.map((x) => (x.id === it.id ? data : x)));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(T("Remove this item from the shopping list?"))) return;
    setBusy(it.id);
    try {
      await client.delete(`${basePath}/shopping-list/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="shopping-list-view">
      {shoppingPage.error && <div role="alert" className="text-sm text-destructive" data-testid="shopping-year-error">{shoppingPage.error} <Button variant="outline" size="sm" onClick={load} data-testid="shopping-year-retry">{T("Retry")}</Button></div>}
      <div className="flex items-center justify-between flex-wrap gap-3 mobile-stack">
        <div>
          <h2 className="management-title font-display text-xl font-bold flex items-center gap-2" data-testid="shopping-list-heading">
            <ShoppingCart className="w-5 h-5" /> {T("Shopping list")}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {neededCount} {neededCount === 1 ? T("item still needed") : T("items still needed")}
          </p>
        </div>
        <div className="ml-auto flex items-center justify-end gap-2 mobile-full" data-testid="shopping-top-actions">
        {yearEnabled && <CalendarYearSelect page={shoppingPage} id="worker-shopping-year" T={T} />}
        <Button variant="outline" size="sm" className="rounded-sm flex-1 sm:flex-none" onClick={load} disabled={loading} title={T("Refresh")} aria-label={T("Refresh")} data-testid="shopping-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${yearEnabled ? "sm:mr-2" : "mr-2"} ${loading ? "animate-spin" : ""}`} /><span className={yearEnabled ? "hidden sm:inline" : ""}>{T("Refresh")}</span>
        </Button>
        <Button size="sm" className="rounded-sm flex-1 sm:flex-none" onClick={() => setAddOpen(true)} data-testid="shopping-add-item">
          <Plus className="w-3.5 h-3.5 mr-2" /> {T("Add item")}
        </Button>
        </div>
      </div>

      <NearbyStoresPanel client={client} basePath={basePath} T={T} />

      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 items-end">
        <div className="col-span-2 sm:col-span-1">
          <div className="text-[10px] mb-1">&nbsp;</div>
          <div className={`relative flex items-center transition-[width] duration-300 ${searchOpen ? "w-full sm:w-[240px]" : "w-11"}`}>
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className="absolute left-0 h-11 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground z-10"
              title={T("Search")}
              data-testid="shopping-search-toggle"
            >
              <Search className="w-4 h-4" />
            </button>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => { if (!search.trim()) setSearchOpen(false); }}
              placeholder={searchOpen ? T("Find a product, note, or colony…") : ""}
              className={`h-11 sm:h-9 rounded-sm pl-11 transition-[width,opacity] duration-300 ${searchOpen ? "w-full sm:w-[240px] pr-8 opacity-100" : "w-11 opacity-0 cursor-pointer"}`}
              data-testid="shopping-search"
            />
            {searchOpen && search && (
              <button
                type="button"
                onClick={() => { setSearch(""); }}
                className="absolute right-2 text-muted-foreground hover:text-foreground"
                title={T("Clear")}
                data-testid="shopping-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="min-w-0 sm:min-w-[220px]">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{T("Colony")}</div>
          <Select value={colonyFilter} onValueChange={setColonyFilter}>
            <SelectTrigger className="h-11 sm:h-9 rounded-sm" data-testid="shopping-colony-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{T("All colonies")}</SelectItem>
              {colonies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5 min-w-0">
          <label className="flex items-center gap-2 text-sm cursor-pointer h-9" data-testid="shopping-sort-colony">
            <Checkbox
              checked={sortByColony}
              onCheckedChange={(v) => setSortByColony(!!v)}
              className="h-5 w-5 rounded-full"
              data-testid="shopping-sort-colony-checkbox"
            />
            {T("Sort by colony")}
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="shopping-hide-purchased">
            <Checkbox
              checked={hidePurchased}
              onCheckedChange={(v) => setHidePurchased(!!v)}
              className="h-5 w-5 rounded-full"
              data-testid="shopping-hide-purchased-checkbox"
            />
            {T("Hide purchased")}
          </label>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-6">{T("Loading…")}</div>
      ) : visible.length === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="shopping-empty">
          <PackageCheck className="w-8 h-8 mx-auto text-muted-foreground" />
          <h3 className="font-display text-lg font-bold mt-2">{T("Nothing on the list")}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {T("Products added from any ticket will show up here.")}
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((it) => {
            const purchased = it.status === "purchased";
            return (
              <div
                key={it.id}
                className={`management-shopping-card border rounded-sm p-3 flex flex-wrap sm:flex-nowrap items-start gap-3 ${purchased ? "border-border bg-secondary/40" : "border-border bg-background"}`}
                data-testid={`shopping-item-${it.id}`}
              >
                <div className="pt-0.5 flex flex-col items-center gap-1.5">
                  <Checkbox
                    checked={purchased}
                    disabled={busy === it.id}
                    onCheckedChange={() => togglePurchased(it)}
                    className="h-5 w-5 rounded-full"
                    data-testid={`shopping-purchased-${it.id}`}
                  />
                  {purchased && (
                    <DropoffManager
                      client={client}
                      basePath={basePath}
                      item={it}
                      required={dropoffRequired}
                      onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
                      T={T}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold ${purchased ? "line-through text-muted-foreground" : ""}`}>
                      {it.product}
                    </span>
                    <ProductTimeline item={it} T={T} />
                    {it.quantity && (
                      <span className="text-xs px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">
                        {it.quantity}
                      </span>
                    )}
                    {purchased && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 font-bold">
                        {T("Purchased")}
                      </span>
                    )}
                    {it.home_depot_product?.product_id && (
                      <a
                        href={it.home_depot_product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 font-bold"
                        data-testid={`shopping-tracked-product-${it.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" /> {T("Exact product tracked")}
                      </a>
                    )}
                  </div>
                  {it.note && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{it.note}</p>}
                  {!purchased && <StoreSearchLinks product={it.product} itemId={it.id} T={T} />}
                  <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <ColonyBadge id={it.colony_id} name={it.colony_name} />
                    <span>
                      {T("Required by")} {it.required_by_name || "—"}
                      {it.required_by_role ? ` (${T(ROLE_LABEL[it.required_by_role] || it.required_by_role)})` : ""}
                      {" · "}{fmtDate(it.created_at)}
                    </span>
                  </div>
                  {purchased && (it.purchased_by || it.purchased_at) && (
                    <div className="text-xs text-emerald-700 mt-0.5" data-testid={`shopping-purchased-by-${it.id}`}>
                      {T("Bought by")} {it.purchased_by || "—"}{it.purchased_at ? ` · ${fmtDate(it.purchased_at)}` : ""}
                    </div>
                  )}
                  {purchased && !((it.dropoff_photo_count || 0) > 0) && !it.dropoff_skipped && !dropoffRequired && (
                    <button
                      type="button"
                      onClick={() => skipDropoff(it)}
                      disabled={busy === it.id}
                      className="text-xs text-muted-foreground underline underline-offset-2 mt-0.5 hover:text-foreground"
                      data-testid={`dropoff-skip-${it.id}`}
                    >
                      {T("Skip drop-off photo")}
                    </button>
                  )}
                  {purchased && !((it.dropoff_photo_count || 0) > 0) && it.dropoff_skipped && (
                    <div className="text-xs text-muted-foreground mt-0.5" data-testid={`dropoff-skipped-${it.id}`}>
                      {T("Drop-off photo skipped")}
                    </div>
                  )}
                  {purchased && !((it.dropoff_photo_count || 0) > 0) && !it.dropoff_skipped && dropoffRequired && (
                    <div className="text-xs text-amber-600 mt-0.5" data-testid={`dropoff-needed-${it.id}`}>
                      {T("Drop-off photo needed")}
                    </div>
                  )}
                </div>
                <div className="flex flex-row sm:flex-col gap-1.5 w-full sm:w-auto justify-end shrink-0">
                  {onOpenTicket && it.ticket_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm h-11 sm:h-8"
                      onClick={() => onOpenTicket(it.ticket_id, it.colony_id)}
                      title={it.ticket_title}
                      data-testid={`shopping-open-ticket-${it.id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {T("Ticket")}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-sm h-11 sm:h-8 w-11 sm:w-auto text-destructive"
                    disabled={busy === it.id}
                    onClick={() => remove(it)}
                    data-testid={`shopping-delete-${it.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddStandaloneShoppingDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        client={client}
        path={`${basePath}/shopping-list`}
        colonies={allColonies.length ? allColonies : colonies}
        onAdded={() => load()}
        T={T}
      />
      <ShoppingAiButton disabled={yearEnabled && (shoppingPage.loading || !shoppingPage.year || !!shoppingPage.error)} calendarYear={yearEnabled ? shoppingPage.year : undefined} client={client} basePath={basePath} T={T} />
    </div>
  );
}

// -----------------------------------------------------------------------
// Compact "N products requested" chip shown on a ticket card so ANYONE
// who can see the ticket (colony contact, worker, office) can see what's
// already been asked for — not gated by the admin/manager-only list view.
// Lazy-loads on click, mirroring LazyPhotoStrip's UX.
// -----------------------------------------------------------------------
export function TicketShoppingStrip({ count = 0, fetchItems, T = (x) => x, testid, client, basePath, inlineActions = false }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!count) return null;

  const load = async () => {
    if (items) { setOpen((o) => !o); return; }
    setBusy(true);
    try {
      setItems((await fetchItems()) || []);
      setOpen(true);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={inlineActions ? "mt-2 sm:contents" : "mt-2"}>
      <button
        type="button"
        onClick={load}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-sm px-2.5 py-1.5 hover:bg-secondary/50 text-muted-foreground ${inlineActions ? "sm:h-8 sm:py-0" : ""}`}
        data-testid={testid ? `${testid}-toggle` : "ticket-shopping-toggle"}
      >
        <ShoppingCart className="w-3.5 h-3.5" />
        {busy
          ? T("Loading…")
          : `${count} ${count > 1 ? T("products requested") : T("product requested")}`}
      </button>
      {open && items && (
        <div className={`mt-2 space-y-1.5 ${inlineActions ? "sm:order-10 sm:basis-full sm:min-w-0" : ""}`} data-testid={testid ? `${testid}-list` : "ticket-shopping-list"}>
          {items.map((it) => (
            <div
              key={it.id}
              className="text-xs border border-border rounded-sm px-2.5 py-1.5 bg-secondary/30 flex items-center gap-2 flex-wrap"
              data-testid={testid ? `${testid}-item-${it.id}` : `ticket-shopping-item-${it.id}`}
            >
              <span className={`font-semibold ${it.status === "purchased" ? "line-through text-muted-foreground" : ""}`}>
                {it.product}
              </span>
              <ProductTimeline item={it} T={T} />
              {it.quantity && <span className="text-muted-foreground">({it.quantity})</span>}
              {it.status === "purchased" ? (
                <span className="text-[10px] uppercase tracking-wider px-1 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 font-bold">
                  {T("Purchased")}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider px-1 py-0.5 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 font-bold">
                  {T("Needed")}
                </span>
              )}
              <span className="text-muted-foreground ml-auto shrink-0">
                {T("by")} {it.required_by_name || "—"}
              </span>
              {it.status === "purchased" && (it.dropoff_photo_count || 0) > 0 && (
                <DropoffPhotoButton client={client} basePath={basePath} itemId={it.id} count={it.dropoff_photo_count} T={T} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Multi-row shopping section embedded INSIDE a ticket reply box, so a
// worker/colony/office can add several products at once while replying.
// Controlled: parent owns the `items` array ([{product, quantity}]).
// -----------------------------------------------------------------------
export function ReplyShoppingSection({ items, setItems, T = (x) => x }) {
  const [open, setOpen] = useState((items || []).length > 0);
  const addRow = () => { setOpen(true); setItems([...(items || []), { product: "", quantity: "" }]); };
  const update = (idx, key, val) => setItems(items.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const removeRow = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    if (next.length === 0) setOpen(false);
  };

  if (!open && (items || []).length === 0) {
    return (
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        data-testid="reply-shopping-add-first"
      >
        <ShoppingCart className="w-3.5 h-3.5" /> {T("Add products to shopping list")}
      </button>
    );
  }

  return (
    <div className="border border-border rounded-sm p-3 space-y-2 bg-secondary/30" data-testid="reply-shopping-section">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
        <ShoppingCart className="w-3.5 h-3.5" /> {T("Products needed (added to shopping list)")}
      </div>
      {(items || []).map((r, idx) => (
        <div key={idx} className="flex gap-2 items-center" data-testid={`reply-shopping-row-${idx}`}>
          <Input
            value={r.product}
            onChange={(e) => update(idx, "product", e.target.value)}
            placeholder={T("Product")}
            className="h-8 rounded-sm flex-1"
            data-testid={`reply-shopping-product-${idx}`}
          />
          <Input
            value={r.quantity}
            onChange={(e) => update(idx, "quantity", e.target.value)}
            placeholder={T("Qty")}
            className="h-8 rounded-sm w-24"
            data-testid={`reply-shopping-qty-${idx}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-sm h-8 px-2 text-destructive"
            onClick={() => removeRow(idx)}
            data-testid={`reply-shopping-remove-${idx}`}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-sm h-8"
        onClick={addRow}
        data-testid="reply-shopping-add-row"
      >
        <Plus className="w-3.5 h-3.5 mr-1.5" /> {T("Add another product")}
      </Button>
    </div>
  );
}

// Post every non-empty product row to the shopping list. Returns count added.
export async function submitShoppingItems(client, path, items) {
  const clean = (items || [])
    .map((i) => ({ product: (i.product || "").trim(), quantity: (i.quantity || "").trim() }))
    .filter((i) => i.product);
  let added = 0;
  for (const it of clean) {
    try { await client.post(path, it); added += 1; } catch (_) { /* skip individual failures */ }
  }
  return added;
}
